import { ROLE_ATTR } from 'router/types';
import { MAIN_STORE_ROUTE_KEYS } from './constants';
import {
  ENUM_COKITCHEN_TEAM,
  ENUM_KITCHEN_ROLES,
  ENUM_MAIN_STORE_ROLES,
  PANEL_ROLES
} from './roles';
import { SVGProps } from 'react';
import {
  InventoryIcon,
  ManageRequestsIcon,
  ProcurementIcon,
  VendorsIcon,
  WasteReportIcon,
  BrandChefsIcon,
  PricingIcon,
  IngredientsIcon,
  ExcelMealIcon,
  AppMealsIcon,
  PaymentTrackerIcon,
  MainStoreIcon
} from 'assets/icons/siderbar-icons';
import { SettingsIcon } from 'cokitchen-icons';
import { PermissionType, RolePermission } from 'models/permission.model';
import { hasPermission, readStoredPermissions, readStoredRole } from './authorization';
import { canAccessRoute } from './route-authorization';
import {
  getModuleScreenBindings,
  getModuleScreenRoutes,
  getRouteModuleBinding
} from './route-module-map';

export const MAIN_STORE_TEAM_ROLES: Record<ENUM_MAIN_STORE_ROLES, ROLE_ATTR> = {
  CUSTOMER_SERVICE: {
    name: 'customer-service',
    baseRoute: MAIN_STORE_ROUTE_KEYS.INVENTORY_REQUESTS,
    routes: Object.values(MAIN_STORE_ROUTE_KEYS)
  },
  CUSTOMER_SERVICE_ADMIN: {
    name: 'customer-service-admin',
    baseRoute: MAIN_STORE_ROUTE_KEYS.INVENTORY_STORE_ITEMS,
    routes: Object.values(MAIN_STORE_ROUTE_KEYS)
  },
  CUSTOMER_SERVICE_LEAD: {
    name: 'customer-service-lead',
    baseRoute: MAIN_STORE_ROUTE_KEYS.INVENTORY_STORE_ITEMS,
    routes: Object.values(MAIN_STORE_ROUTE_KEYS)
  },
  CUSTOMER_SERVICE_INBOUND: {
    name: 'customer-service-lead',
    baseRoute: MAIN_STORE_ROUTE_KEYS.PROCUREMENT,
    routes: Object.values(MAIN_STORE_ROUTE_KEYS)
  },
  CUSTOMER_SERVICE_OUTBOUND: {
    name: 'customer-service-lead',
    baseRoute: MAIN_STORE_ROUTE_KEYS.PROCUREMENT,
    routes: Object.values(MAIN_STORE_ROUTE_KEYS)
  },
  FRONT_DESK: {
    name: 'front-desk',
    baseRoute: MAIN_STORE_ROUTE_KEYS.INVENTORY_STOCK,
    routes: Object.values(MAIN_STORE_ROUTE_KEYS)
  }
};

const KITCHEN_TEAM_ROLES: Record<ENUM_KITCHEN_ROLES, ROLE_ATTR> = {
  KITCHEN_ADMIN: {
    name: 'kitchen-admin',
    baseRoute: MAIN_STORE_ROUTE_KEYS.KITCHEN_PRODUCTION_REPORT,
    routes: Object.values(MAIN_STORE_ROUTE_KEYS)
  }
};

export const getCokitchenTeam = () => {
  const role = (readStoredRole()?.name ?? '') as PANEL_ROLES;
  const allRoles: Record<ENUM_COKITCHEN_TEAM, PANEL_ROLES[]> = {
    'main-store': Object.values(ENUM_MAIN_STORE_ROLES),
    kitchen: Object.values(ENUM_KITCHEN_ROLES)
  };
  for (let i = 0; i < Object.entries(allRoles).length; i++) {
    const [key, values] = Object.entries(allRoles)[i];
    const roleValue = values.find((v) => v === role);
    if (roleValue) {
      return key as ENUM_COKITCHEN_TEAM;
    }
  }
  return ENUM_COKITCHEN_TEAM.MAIN_STORE;
};

export const getCurrentTeam = (): ENUM_COKITCHEN_TEAM => {
  return getCokitchenTeam();
};

export const getTeamRoles = (): Record<string, ROLE_ATTR> => {
  const mapping: Record<ENUM_COKITCHEN_TEAM, any> = {
    'main-store': MAIN_STORE_TEAM_ROLES,
    kitchen: KITCHEN_TEAM_ROLES
  };
  const currentTeam = getCurrentTeam();
  return mapping[currentTeam];
};

type SidebarLinkType = {
  name: string;
  path?: string;
  underline?: boolean;
  dropdownList?: SidebarLinkType[];
  icon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
};

type SidebarStructureType = {
  top: SidebarLinkType[];
  bottom: SidebarLinkType[];
};

const SIDEBAR_LINK_MAP: Record<ENUM_COKITCHEN_TEAM, SidebarStructureType> = {
  'main-store': {
    top: [
      {
        name: 'Main store',
        icon: MainStoreIcon,
        dropdownList: [
          {
            name: 'Inventory',
            icon: InventoryIcon,
            dropdownList: [
              {
                name: 'Store items',
                path: MAIN_STORE_ROUTE_KEYS.INVENTORY_STOCK
              },
              {
                name: 'Ingredients',
                path: MAIN_STORE_ROUTE_KEYS.INVENTORY_STORE_ITEMS
              },
              {
                name: 'Minimum stock settings',
                path: MAIN_STORE_ROUTE_KEYS.MAIN_STORE_MINIMUM_STOCK_SETTINGS
              }
            ]
          },

          {
            name: 'Manage Requests',
            icon: ManageRequestsIcon,
            dropdownList: [
              {
                name: 'Internal procurement request',
                path: MAIN_STORE_ROUTE_KEYS.ITEM_REQUESTS
              }
            ]
          },
          {
            name: 'Procurement',
            icon: ProcurementIcon,
            path: MAIN_STORE_ROUTE_KEYS.PROCUREMENT
          },
          {
            name: 'Transfer',
            icon: ProcurementIcon,
            path: MAIN_STORE_ROUTE_KEYS.TRANSFER
          },
          {
            name: 'Waste Report',
            icon: WasteReportIcon,
            underline: true,
            path: MAIN_STORE_ROUTE_KEYS.WASTE_REPORT
          },
          {
            name: 'Vendors',
            icon: VendorsIcon,
            path: MAIN_STORE_ROUTE_KEYS.VENDORS,
            underline: true
          }
        ]
      },
      {
        name: 'Kitchen store',
        icon: MainStoreIcon,
        dropdownList: [
          {
            name: 'Inventory',
            icon: InventoryIcon,
            dropdownList: [
              {
                name: 'Store items',
                path: MAIN_STORE_ROUTE_KEYS.KITCHEN_INVENTORY_STOCK
              },
              {
                name: 'Prep mixes store items',
                path: MAIN_STORE_ROUTE_KEYS.STOCK_PREP_TEAM
              },
              {
                name: 'Production meals store items',
                path: MAIN_STORE_ROUTE_KEYS.STOCK_BRAND_CHEF_TEAM
              },
              {
                name: 'Minimum stock settings',
                path: MAIN_STORE_ROUTE_KEYS.KITCHEN_MINIMUM_STOCK_SETTINGS
              }
            ]
          },

          {
            name: 'Manage Requests',
            icon: ManageRequestsIcon,
            dropdownList: [
              {
                name: 'Production meal request',
                path: MAIN_STORE_ROUTE_KEYS.KITCHEN_MANAGE_ITEM_REQUESTS
              },
              {
                name: 'Prep mix request',
                path: MAIN_STORE_ROUTE_KEYS.KITCHEN_MANAGE_PREP_TEAM_REQUESTS
              }
            ]
          },
          {
            name: 'Internal Procurement',
            icon: ProcurementIcon,
            path: MAIN_STORE_ROUTE_KEYS.KITCHEN_PROCUREMENT
          },
          {
            name: 'Transfer',
            icon: ProcurementIcon,
            path: MAIN_STORE_ROUTE_KEYS.KITCHEN_TRANSFER
          },
          {
            name: 'Waste Report',
            icon: WasteReportIcon,
            underline: true,
            path: MAIN_STORE_ROUTE_KEYS.KITCHEN_WASTE_REPORT
          }
        ]
      },
      {
        name: 'Prep Team',
        icon: BrandChefsIcon,
        dropdownList: [
          {
            name: 'Kitchen Requests',
            path: MAIN_STORE_ROUTE_KEYS.PREP_REQUESTS
          }
        ]
      },
      {
        name: 'Production Team',
        icon: BrandChefsIcon,
        dropdownList: [
          {
            name: 'Manage requests',
            path: MAIN_STORE_ROUTE_KEYS.MANAGE_REQUESTS
          }
        ]
      },
      {
        name: 'Executive',
        icon: BrandChefsIcon,
        dropdownList: [
          {
            name: 'Procurements',
            path: MAIN_STORE_ROUTE_KEYS.EXECUTIVE_PROCUREMENTS
          }
        ]
      },

      {
        name: 'Pricing',
        icon: PricingIcon,
        dropdownList: [
          {
            name: 'Ingredients',
            icon: IngredientsIcon,
            path: MAIN_STORE_ROUTE_KEYS.INGREDIENT_LIST
          },
          {
            name: 'Recipe',
            icon: ExcelMealIcon,
            dropdownList: [
              {
                name: 'Production meals recipe config..',
                path: MAIN_STORE_ROUTE_KEYS.EXCEL_MEAL_CONFIGURATION
              },
              {
                name: 'Prep meals recipe config..',
                path: MAIN_STORE_ROUTE_KEYS.EXCEL_PREP_CONFIGURATION
              }
              // {
              //   name: 'Pricing',
              //   path: MAIN_STORE_ROUTE_KEYS.EXCEL_MEAL_PRICING
              // }
            ]
          },
          {
            name: 'Base meals',
            icon: AppMealsIcon,
            path: MAIN_STORE_ROUTE_KEYS.BASE_MEALS
          }
          // {
          //   name: 'Cost setting',
          //   icon: CostSettingIcon,
          //   path: MAIN_STORE_ROUTE_KEYS.COST_SETTINGS
          // }
        ]
      },
      {
        name: 'Settings',
        icon: SettingsIcon,
        dropdownList: [
          {
            name: 'Manage users',
            path: MAIN_STORE_ROUTE_KEYS.MANAGE_USERS
          }
        ]
      },
      {
        name: 'Payment tracker',
        icon: PaymentTrackerIcon,
        path: MAIN_STORE_ROUTE_KEYS.PAYMNET_TRACKER,
        underline: true
      }
    ],
    bottom: []
  },

  kitchen: {
    top: [],
    bottom: []
  }
};

const filterSidebarLinks = (
  menuItems: SidebarLinkType[],
  permissions: PermissionType[]
): SidebarLinkType[] => {
  const canRenderSidebarPath = (path?: string) => {
    if (!path) return true;
    if (!permissions.length) return true;

    const binding = getRouteModuleBinding(path);
    if (!binding) return false;

    const moduleScreens = getModuleScreenBindings(binding.module);
    const moduleScreenRoutes = getModuleScreenRoutes(binding.module);
    if (!moduleScreens.length || !moduleScreenRoutes.length) return false;

    return hasPermission(permissions, binding.module, [RolePermission.Read, RolePermission.Write]);
  };

  return menuItems.reduce<SidebarLinkType[]>((acc, item) => {
    if (
      item.path &&
      (!canAccessRoute(item.path, permissions, { requireMappedRule: true }) ||
        !canRenderSidebarPath(item.path))
    ) {
      return acc;
    }

    if (item.dropdownList?.length) {
      const filteredDropdownList = filterSidebarLinks(item.dropdownList, permissions);
      if (filteredDropdownList.length === 0 && !item.path) {
        return acc;
      }
      acc.push({ ...item, dropdownList: filteredDropdownList });
      return acc;
    }

    acc.push(item);
    return acc;
  }, []);
};

export const getSidebarLinks = (
  permissionsArg?: PermissionType[],
  options?: { isMainAdmin?: boolean }
): SidebarStructureType => {
  const team = getCurrentTeam();
  const menu = SIDEBAR_LINK_MAP[team] ?? { top: [], bottom: [] };
  if (options?.isMainAdmin) return menu;

  const permissions = permissionsArg ?? readStoredPermissions();
  if (!permissions.length) {
    return {
      top: [],
      bottom: []
    };
  }

  return {
    top: filterSidebarLinks(menu.top, permissions),
    bottom: filterSidebarLinks(menu.bottom, permissions)
  };
};
const getKitchenRoutes = (role: PANEL_ROLES) => {
  return KITCHEN_TEAM_ROLES[role as keyof typeof KITCHEN_TEAM_ROLES];
};
const getMainStoreRoutes = (role: PANEL_ROLES) => {
  return MAIN_STORE_TEAM_ROLES[role as keyof typeof MAIN_STORE_TEAM_ROLES];
};

export const getRoleRoutes = () => {
  const role = (readStoredRole()?.name ?? '') as PANEL_ROLES;
  const teamMapping: Record<ENUM_COKITCHEN_TEAM, (role: PANEL_ROLES) => ROLE_ATTR> = {
    'main-store': getMainStoreRoutes,
    kitchen: getKitchenRoutes
  };
  const currentTeam = getCurrentTeam();
  const routeResolver = teamMapping[currentTeam];
  if (!routeResolver) return [];
  const routeConfig = routeResolver(role);
  return routeConfig?.routes ?? [];
};
